<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Create customer') }}</h1>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading.form"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Customer details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Customer details and classification') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <form id="customerForm" @submit.prevent="saveCustomer">
                                <div class="grid grid-cols-3 gap-6">
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="customerName">{{ $t('Name') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="customerName"
                                                v-model="customer.name"
                                                :placeholder="$t('Name')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.name" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.name }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="customerPhone">{{ $t('Phone') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <span class="text-gray-500 sm:text-sm">+52</span>
                                            </div>
                                            <input
                                                id="customerPhone"
                                                v-model="customer.phone"
                                                :placeholder="$t('Phone')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full pl-12 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                maxlength="10"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.phone" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.phone }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="customerNeighborhood">{{ $t('Ticket neighborhood') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input-select
                                                id="customerNeighborhood"
                                                v-model="customer.neighborhood_id"
                                                :options="neighborhoodList"
                                                option-label="name"
                                                searchable
                                            >
                                                <template v-slot:selectedOption="props">
                                                    <template v-if="props && !props.anySelected">
                                                        <span class="block truncate">{{ $t('Select an option') }}</span>
                                                    </template>
                                                    <template v-else>
                                                        <span class="block truncate">{{ props.option.name }}</span>
                                                    </template>
                                                </template>
    
                                                <template v-slot:selectOption="props">
                                                    <div :class="props.option.id === customer.neighborhood_id ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                </template>
                                            </input-select>
                                        </div>
                                        <div v-if="errors.neighborhood_id" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.neighborhood_id }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="street">{{ $t('Ticket street') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="street"
                                                v-model="customer.street"
                                                :placeholder="$t('Ticket street')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.street" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.street }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="letter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="letter"
                                                v-model="customer.letter"
                                                :placeholder="$t('Ticket letter')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.letter" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="number">{{ $t('Ticket number') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="number"
                                                v-model="customer.number"
                                                :placeholder="$t('Ticket number')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.number" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.number }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="letter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="letter"
                                                v-model="customer.letter2"
                                                :placeholder="$t('Ticket letter')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.letter2" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter2 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="crossover">{{ $t('Crossover') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="crossover"
                                                v-model="customer.crossover"
                                                :placeholder="$t('Crossover')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            />
                                        </div>
                                        <div v-if="errors.crossover" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.crossover }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="letter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="letter"
                                                v-model="customer.letter3"
                                                :placeholder="$t('Ticket letter')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.letter3" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter3 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="crossover2">{{ $t('Crossover2') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="crossover2"
                                                v-model="customer.crossover2"
                                                :placeholder="$t('Crossover2')"
                                                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            />
                                        </div>
                                        <div v-if="errors.crossover2" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.crossover2 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="letter">{{ $t('Ticket letter') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="letter"
                                                v-model="customer.letter4"
                                                :placeholder="$t('Ticket letter')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.letter4" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.letter4 }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="address">{{ $t('Address') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="address"
                                                v-model="customer.address"
                                                :placeholder="$t('Address')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.address" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.address }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="mapUrl">{{ $t('Map url') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="mapUrl"
                                                v-model="customer.map_url"
                                                :placeholder="$t('Map Url')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            >
                                        </div>
                                        <div v-if="errors.map_url" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.map_url }}</p>
                                        </div>
                                    </div>
                                    <div class="col-span-3">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="referency">{{ $t('Referency') }}</label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="referency"
                                                v-model="customer.referency"
                                                :placeholder="$t('Referency')"
                                                class="disabled:bg-gray-200 disabled:cursor-not-allowed form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                                v-uppercase
                                            >
                                        </div>
                                        <div v-if="errors.referency" class="col-span-3">
                                            <p class="text-red-600 text-sm mt-2">{{ errors.referency }}</p>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <div class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/admin/customers"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                form="customerForm"
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Create customer') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
    </main>
</template>

<script>
import { TheMask } from 'vue-the-mask';
import * as yup from 'yup';

export default {
    name: "new",
    components: {
        TheMask
    },
    metaInfo() {
        return {
            title: this.$i18n.t('Create ticket')
        }
    },
    data() {
        return {
            loading: {
                form: true,
                file: false,
            },
            uploadingFileProgress: 0,
            neighborhoodList: [],
            customer: {
                name: '',
                email: '',
                neighborhood_id: null,
                phone: '',
                street: '',
                letter: '',
                letter2: '',
                letter3: '',
                letter4: '',
                number: '',
                address: '',
                map_url: '',
                crossover: null,
                crossover2: null,
                referency: ""
            },
            errors: {}
        }
    },
    mounted() {
        this.getFilters();
    },
    watch: {
        customer: {
            handler() {
                this.validateAllFieldsCustomer()
            },
            deep: true
        }
    },
    methods: {
        getFilters() {
            const self = this;
            self.loading.form = true;
            axios.get('api/dashboard/tickets/filters').then(function (response) {
                self.neighborhoodList = response.data.neighborhoodList;
                self.loading.form = false;
            }).catch(function () {
                self.loading.form = false;
            })
        },
        async saveCustomer() {
            const self = this;

            await self.validateAllFieldsCustomer();
            
            if (Object.values(self.errors).some(error => error)) return;

            self.loading.form = true;
            
            const customer = { ...self.customer };
            customer.phone = !customer.phone.startsWith('+52') ? `+52${customer.phone}` : customer.phone;

            axios.post('api/dashboard/admin/customers', customer).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/admin/customers/' + response.data.ticket.uuid + '/manage');
            }).catch(function () {
                self.loading.form = false;
            });
        },
        async validateAllFieldsCustomer() {
            const customerFormSchema = yup.object().shape({
                name: yup.string().required('El nombre es obligatorio.'),
                // email: yup.string().required('El correo es obligatorio.').email('Debe ser un correo electrónico válido.'),
                phone: yup.string()
                    .matches(/^(\+52)?[1-9][0-9]{9}$/, 'Número de teléfono no válido. Debe ser un número de 10 dígitos que comience con un número válido.')
                    .required('El teléfono es obligatorio.'),
                neighborhood_id: yup.string().required('La colonia es obligatorio'),
                street: yup.string().required('La calle es obligatorio.'),
                letter: yup.string(),
                letter2: yup.string(),
                letter3: yup.string(),
                letter4: yup.string(),
                number: yup.string().required('El número es obligatorio.'),
                address: yup.string().required('La dirección es obligatorio.'),
                map_url: yup.string(),
                crossover: yup.string().required('El cruzamiento 1 es obligatorio.'),
                crossover2: yup.string().nullable(),
                referency: yup.string()
            });

            try {
                await customerFormSchema.validate(this.customer, { abortEarly: false });
                Object.keys(this.errors).forEach(field => {
                    this.$set(this.errors, field, '');
                });
            } catch (validationErrors) {
                Object.keys(this.errors).forEach(field => {
                    this.$set(this.errors, field, '');
                });
                validationErrors.inner.forEach(error => {
                    this.$set(this.errors, error.path, error.message);
                });
            }
        }
    },
}
</script>
